import { useCallback } from "react";
import styled from "styled-components";
import { LockClosedIcon } from "@heroicons/react/24/outline";
import { Button } from "@sablier/v2-components/molecules";
import { tags } from "@sablier/v2-constants";
import { useT } from "@sablier/v2-locales";
import { vendors } from "@sablier/v2-utils";
import { rgba } from "polished";
import {
  Approval,
  Proxy,
  Warning as Required,
  Total,
} from "~/client/components";
import { useAccount, useCreateGroupField } from "~/client/hooks";
import { useFieldStreams, useFormPrepare, useFormSubmit } from "../logic";

const WrapperPartial = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    row-gap: calc(${(props) => props.theme.sizes.edge} * 2);
    width: 100%;
  }
`;

const ButtonWithGlow = styled(Button)`
  box-shadow: 0 12px 48px -15px ${(props) => rgba(props.theme.colors.primaryMiddle, 0)};
`;

const Wrapper = styled(WrapperPartial)`
  & > * {
    width: 100%;
  }
`;

interface Props {}

function Confirmation({}: Props) {
  const token = useCreateGroupField("token");
  const { total } = useFieldStreams();
  const { allowance, isPrepared, hasProxy } = useFormPrepare();
  const { isConnecting, isConnected } = useAccount();
  const { doSubmit } = useFormSubmit();
  const { t } = useT();

  const onClick = useCallback(() => {
    vendors.track.log("createGroupStream");
    doSubmit();
  }, [doSubmit]);

  return (
    <Wrapper>
      <Total amount={total} token={token.field.value} />
      {!isConnecting && !isConnected && <Required.Connect />}

      {hasProxy ? (
        <Approval allowance={allowance} purpose={"group"} />
      ) : (
        <Proxy />
      )}

      {isPrepared ? (
        <ButtonWithGlow
          accent={"primary"}
          appearance={"gradient"}
          onClick={onClick}
          title={t("structs.createGroupOfStreams")}
          cy={tags.actionCreateGroup}
        />
      ) : (
        <Button
          accent={"primary"}
          appearance={"gradient"}
          isDisabled={"soft"}
          left={LockClosedIcon}
          title={t("structs.createGroupOfStreams")}
          cy={tags.actionCreateGroup}
        />
      )}
    </Wrapper>
  );
}

export default Confirmation;
